// Ban a player from where you are already looking at them.
//
// The ban tool lives on its own page, so removing somebody you have just spotted
// meant copying their username, navigating away, searching for them, filling a
// form and coming back. That is a lot of steps to ask of whoever is on call
// during an event, and every one of them is a chance to ban the wrong account.
//
// This bans the exact user_id on the row, so there is no name to mistype.
//
// Split into a button and a confirmation on purpose: the button belongs in the
// table cell, the confirmation does not. A ConfirmBar inside a cell of a
// `whitespace-nowrap` table cannot wrap, so it stretched the column and pushed
// the whole page sideways. The caller renders the confirmation outside the
// table, where it has the full width to use.
//
// Note what a ban does and does not do: it is checked on login *and* on every
// join, so a banned player cannot enter another match anywhere, immediately.
// It does not remove them from a match already in progress: ban blocks the next
// login, it does not end the current session. Use Kick alongside it for that.
//
// That used to read "nothing does, because the game server calls Django and
// never the other way around" -- true until a kick could ride back on the
// response to sync_online_players. Restarting the server to remove one player is
// no longer the advice; it ends everybody else's match too.

import { faBan } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import { ConfirmBar } from '~/team_tools/components/ConfirmBar'
import { useNotification } from '~/team_tools/components/NotificationProvider'
import { banPlayer } from './api'

// Mirrors Ban.REASON_CHOICES.
const REASONS = [
    { value: '1', label: 'Exploits' },
    { value: '2', label: 'Spam' },
    { value: '3', label: 'Threats' },
    { value: '4', label: 'Offensive language' },
    { value: '101', label: 'Other' },
]

export const BanButton = ({ username, onAsk }: { username: string; onAsk: () => void }) => (
    <button
        onClick={onAsk}
        title={`Ban ${username}. Blocks login and every join, immediately`}
        className='rounded border border-red-500/40 px-2 py-0.5 text-[10px] uppercase tracking-wide text-red-400 hover:bg-red-500/10'
    >
        <FontAwesomeIcon icon={faBan} className='mr-1' />
        Ban
    </button>
)

export const BanConfirm = ({
    userId,
    username,
    onDone,
    onCancel,
}: {
    userId: number
    username: string
    onDone?: () => void
    onCancel: () => void
}) => {
    const [busy, setBusy] = useState(false)
    // Two states. Ban stays the default here: this is the in-the-moment
    // tool, reached for while somebody is visibly ruining a match.
    const [kind, setKind] = useState<1 | 2>(2)
    const { addNotification } = useNotification()

    const run = async (reason?: string) => {
        setBusy(true)
        try {
            // Permanent, because this is the in-the-moment tool: an operator
            // reaching for it during an event wants the player gone now, and a
            // permanent ban can be lifted from the Ban admin afterwards.
            // Getting a duration wrong in a hurry is the worse mistake.
            await banPlayer({
                user_id: userId,
                kind,
                reason: Number(reason ?? 101),
                // A pause has no length; it ends when somebody lifts it.
                is_permanent: kind === 2,
                comment:
                    kind === 1
                        ? 'Paused from Server Control while the player was online'
                        : 'Banned from Server Control while the player was online',
            })
            addNotification(
                kind === 1
                    ? `${username} is paused and cannot rejoin`
                    : `${username} is banned and cannot rejoin`,
                'success'
            )
            onDone?.()
        } catch (err: any) {
            addNotification(err.message, 'error')
        } finally {
            setBusy(false)
            onCancel()
        }
    }

    return (
        // whitespace-normal because this may sit near a nowrap table; without
        // it the detail text refuses to wrap and the bar runs off the page.
        <div className='w-full min-w-0 whitespace-normal'>
            {/* The same two states as every other ban control, so an operator
                who is not sure has something to reach for other than the
                permanent one. */}
            <div className='mb-2 flex flex-row gap-2 text-xs'>
                <button
                    type='button'
                    onClick={() => setKind(1)}
                    className={`flex-1 px-2 py-1 rounded border ${
                        kind === 1 ? 'border-amber-400 text-amber-400' : 'border-white/20 opacity-50'
                    }`}
                >
                    Pause — investigating
                </button>
                <button
                    type='button'
                    onClick={() => setKind(2)}
                    className={`flex-1 px-2 py-1 rounded border ${
                        kind === 2 ? 'border-red-400 text-red-400' : 'border-white/20 opacity-50'
                    }`}
                >
                    Ban — they cheated
                </button>
            </div>
            <ConfirmBar
                message={kind === 1 ? `Pause ${username}?` : `Ban ${username}?`}
                detail={
                    'Takes effect immediately: they cannot log in or join any match. ' +
                    'It does not remove them from the match they are in. Restart the ' +
                    'server for that. Permanent; lift it from Bans in the admin.'
                }
                confirmLabel='Ban player'
                options={REASONS.map((r) => ({ ...r, danger: true }))}
                defaultOption='1'
                onConfirm={run}
                onCancel={onCancel}
                busy={busy}
            />
        </div>
    )
}
